/**
 * O espacial do CPTu: o plano local, o valor de uma vertical numa cota, o variograma (experimental e
 * ajustado), o IDW, a krigagem ordinária, a seção ao longo de uma linha, o COV por cota, a grade de
 * voxels e o variograma das cotas — spec 07 §5.7 e §11; onda 316.
 *
 * MODELO INTERPRETATIVO, FORA DO RN-252. Nada daqui é medida: entre as verticais o valor é ESTIMADO
 * (RN-C07 da spec 02), e não há coluna do CPeT-IT para conferir. Por isso este arquivo não tem gabarito,
 * não entra na conformidade (`conformidade.ts`) e não muda número nenhum do motor: `MOTOR_CPTU_VERSAO` é o
 * mesmo. O que vai ao PDF e ao Portal vai como estimativa, com o método e os parâmetros escritos ao lado.
 *
 * De onde vêm os métodos (a origem; a forma exata de cada conta é a do motor, abaixo):
 *  · o plano local equirretangular no centroide (x = R·Δλ·cos φ₀, y = R·Δφ), com o raio médio da Terra
 *    R = 6 371 008,8 m (o raio médio do elipsoide GRS80/WGS84, (2a + b)/3), bom a 1 mm em campanhas de
 *    poucos quilômetros;
 *  · o inverso da distância (IDW, Shepard 1968, «A two-dimensional interpolation function for
 *    irregularly-spaced data»), potência 2 de partida;
 *  · a krigagem ordinária e o variograma (Matheron 1963, «Principles of geostatistics»; Journel &
 *    Huijbregts 1978, «Mining geostatistics»): o sistema (n + 1) × (n + 1) com o multiplicador de
 *    Lagrange, os modelos esférico e exponencial, a variância de krigagem σ² = Σ λᵢ γ(hᵢ₀) + μ;
 *  · o ajuste do variograma por mínimos quadrados ponderados (Cressie 1985, «Fitting variogram models by
 *    weighted least squares»): minimizar Σ N(h)·[γ̂(h)/γ(h; θ) − 1]².
 *
 * O que as fontes não fixam, o motor escolheu — cada item é ESCOLHA DO MOTOR, SEM FONTE — MODELO
 * INTERPRETATIVO, FORA DO RN-252 (ruling da 316.1, R10 da 316):
 *
 *  1. Verticais no MESMO LUGAR entram pela média: dois pontos a menos de `LUGAR_IGUAL` (0,01 m) em planta
 *     — ou, na seção, na mesma distância ao longo da linha — viram um ponto com a média dos valores com
 *     número, antes de interpolar (`agruparPorLugar`). É o que a seção, a grade e o variograma das cotas
 *     fazem; `krigagemOrdinaria` crua, com dois pontos no mesmo lugar, tem sistema singular e devolve
 *     NaN (nunca exceção); o `idw` cru, no próprio lugar de dois pontos, devolve a média deles.
 *  2. γ(0) = 0 no modelo (a diagonal do sistema), e no PRÓPRIO ponto dado a krigagem devolve o valor
 *     dele com variância = pepita (a pepita é o que não se resolve, nem no ponto medido); o IDW, no
 *     próprio ponto, devolve o valor dele. O alcance do exponencial é o PRÁTICO: γ(alcance) = pepita +
 *     95 % da contribuição, γ(h) = pepita + c·(1 − e^(−3h/a)). Patamar = pepita + contribuição.
 *  3. O variograma experimental tem `classes` classes de largura igual, de 0 a hMax; o par a exatamente
 *     hMax cai na última; o par além de hMax fica fora. A classe vazia fica na lista com pares 0, γ NaN e
 *     h no meio dela — a tela precisa ver o buraco. γ̂(h) = Σ(Δv)²/(2n), h = a média das distâncias dos
 *     pares da classe.
 *  4. hMax, quando não vem, é METADE DA MAIOR DISTÂNCIA entre os pontos (a regra usual: além da metade
 *     os pares são poucos e o experimental mente).
 *  5. O ajuste (`ajustarVariograma`) minimiza o critério de Cressie por BUSCA EM GRADE (21 × 21 × 25 em
 *     pepita, contribuição e alcance) e REFINO (24 voltas de busca local com o passo caindo pela metade),
 *     determinístico, sem sorteio. A pepita vai de 0 ao maior γ̂; a contribuição de 0 a 2 × o maior γ̂; o
 *     alcance de uma fração do menor h (`FRACAO_DO_ALCANCE_MINIMO`) a 2 × o maior h com pares. Com menos
 *     de 3 classes com pares não ajusta (`ajustado: false`, com motivo). A PEPITA PURA (contribuição 0,
 *     o modelo constante) tem o custo em forma fechada — o mínimo de Σ n (γ̂/p − 1)² é p = Σ n γ̂² / Σ n γ̂
 *     — e GANHA NO EMPATE com o melhor da busca (custo ≤ o dele, a 1e-9 relativo): abaixo da menor
 *     distância com pares o modelo já está no patamar em todo h, e os dados não distinguem «pepita p» de
 *     «pepita 0, contribuição p, alcance minúsculo»; no empate fica a hipótese mais simples, com
 *     `pepita = patamar = p`, o alcance = o maior h com pares (um número finito, só para a tela e para
 *     `gamaDoModelo`: na pepita pura o alcance não muda γ) e o motivo «o patamar ficou na pepita: sem
 *     estrutura espacial nos dados». Quando o alcance fica no limite de cima da busca, o motivo diz: os
 *     dados não definem o alcance (os pares acabam antes de a curva dobrar); no limite de baixo, o
 *     mesmo (a curva dobra antes do primeiro par). A classe com γ̂ = 0 pesa como as outras (o modelo no
 *     denominador, não o dado).
 *  6. A seção (`secaoAoLongoDaLinha`): cada vertical projeta no TRECHO MAIS PERTO da polilinha (no
 *     empate, o primeiro), com a distância ao longo (os trechos anteriores + a projeção) e o afastamento;
 *     entram as de afastamento ≤ máximo (30 m de partida, spec §5.7). A linha com um vértice sem número
 *     dá a seção VAZIA (não se emenda os vizinhos: seria outra linha). As estações vão de 0 ao fim da
 *     linha de `passoHorizontal` em `passoHorizontal`, com a última no fim exato da linha (acrescentada
 *     quando falta mais de 1 % do passo, senão no lugar da última regular); as cotas, da
 *     BOCA MAIS ALTA das verticais que entram ao fundo mais fundo delas, de `passoVertical` em
 *     `passoVertical`, começando na boca (não num múltiplo do passo). Em cada cota, interpola em 1D ao
 *     longo da linha com as verticais que têm valor ali (uma só vale em toda a linha); sem nenhuma, NaN.
 *     O COV da cota (`Secao.cov`) usa as verticais CRUAS — duas no mesmo lugar contam duas vezes,
 *     porque o COV mede a dispersão das medidas —, enquanto a interpolação usa a média delas (escolha 1).
 *     O motor não corta nada fora das estações das verticais: quem recorta é a tela.
 *  7. A grade (`gradeDeVoxels`): a caixa das verticais em planta mais a margem (meio passo em x e em y
 *     quando não vem) — a grade COBRE a caixa: o último nó fica em máx + margem ou além (aquém, no
 *     máximo, de `LUGAR_IGUAL`, o mesmo lugar da escolha 1), com sobra menor que um passo quando a
 *     largura não é múltipla dele —, a origem no canto de menor x, menor y e
 *     MENOR cota; as cotas da boca mais alta para baixo, de `passo.z` em `passo.z` (o topo exatamente na
 *     boca mais alta). Em cada cota, interpolação 2D com as verticais que têm valor ali; sem nenhuma, a
 *     camada é NaN. Acima de `LIMITE_DE_VOXELS` voxels (e, na seção, de `LIMITE_DE_CELULAS_DA_SECAO`
 *     células) o motor devolve vazio — a tela diz o tamanho e pede outro passo. Na krigagem, a matriz Γ
 *     das verticais com valor na cota é FATORADA UMA VEZ POR COTA (LU com pivô parcial) e cada alvo só
 *     resolve o lado direito — o mesmo número da `krigagemOrdinaria` alvo a alvo.
 *  8. O variograma das cotas (`variogramaDasCotas`): as cotas de `passoVertical` em `passoVertical`, da
 *     boca mais alta ao fundo mais fundo (começando na boca); em cada uma, o experimental das verticais
 *     com valor ali, com a distância no plano local das verticais e o MESMO hMax (metade da maior
 *     distância em planta, ou o dado); as `classes` classes somadas pelos pares, com h e γ médios
 *     ponderados pelos pares — todas as classes, as vazias como no experimental (pares 0, γ NaN, h no
 *     meio). Com menos de dois lugares distintos, [].
 *
 * Como o resto do motor: sem dependência, sem relógio, sem sorteio; «não se aplica» é NaN, nunca
 * Infinity; nada lança — o parâmetro que não serve devolve vazio ou NaN.
 */
import type { Serie } from './tipos';

/** Um ponto no plano local, em m. */
export interface PontoPlano { x: number; y: number }

/** Uma vertical no espaço: a coordenada, a cota da boca (m) e uma grandeza ao longo da profundidade (m). */
export interface VerticalEspacial {
  codigo: string;
  latitude: number;
  longitude: number;
  /** m, a boca da vertical. */
  cota: number;
  /** m, crescente. */
  profundidade: ArrayLike<number>;
  /** NaN = sem valor. */
  valor: ArrayLike<number>;
}

/** O plano local equirretangular no centroide das verticais. */
export interface PlanoLocal {
  lat0: number;
  lon0: number;
  paraPlano(lat: number, lon: number): PontoPlano;
  paraGeo(p: PontoPlano): { latitude: number; longitude: number };
}

export type ModeloDeVariograma = 'esferico' | 'exponencial';

/** Um modelo de variograma: patamar = pepita + contribuição; alcance prático no exponencial (escolha 2). */
export interface Variograma {
  modelo: ModeloDeVariograma;
  pepita: number;
  patamar: number;
  alcance: number;
}

/** Uma classe do variograma experimental: a distância média, a semivariância e quantos pares. */
export interface ParExperimental { h: number; gama: number; pares: number }

export type MetodoEspacial = { tipo: 'idw'; potencia?: number } | { tipo: 'krigagem'; variograma: Variograma };

/** A seção: `valores` tem cotas.length × distancias.length, linha a linha (uma linha por cota). */
export interface Secao {
  distancias: Serie;
  cotas: Serie;
  valores: Serie;
  verticais: { codigo: string; distancia: number; afastamento: number }[];
  /** O COV das verticais com valor, por cota. */
  cov: Serie;
}

/**
 * A grade de voxels: índice = ix + nx·(iy + ny·iz); NaN onde não há estimativa. O nó (ix, iy, iz) fica em
 * x = origem.x + ix·passo.x, y = origem.y + iy·passo.y, cota = origem.cota + iz·passo.z — a convenção
 * destra, com `iz` crescendo PARA CIMA. `origem.cota` é o nó mais baixo: ≥ o fundo mais fundo das
 * verticais, até um passo acima dele; o topo, origem.cota + (n.z − 1)·passo.z, é exatamente a boca mais
 * alta. `n` conta NÓS (vértices), não células: quem decide se o voxel é centrado no nó é quem desenha.
 */
export interface GradeDeVoxels {
  /** O nó de menor x, menor y e menor cota. */
  origem: PontoPlano & { cota: number };
  passo: { x: number; y: number; z: number };
  /** Quantos nós em cada eixo. */
  n: { x: number; y: number; z: number };
  valores: Float32Array;
}

/** Um ponto com valor, a entrada do IDW, da krigagem e do variograma experimental. */
type PontoComValor = { p: PontoPlano; valor: number };

/** O raio médio da Terra, m. */
const RAIO_DA_TERRA = 6371008.8;

const GRAUS = Math.PI / 180;

/** Afastamento máximo da linha, m (spec §5.7). */
const AFASTAMENTO_MAXIMO_PADRAO = 30;

/** A potência do IDW de partida. */
const POTENCIA_PADRAO = 2;

/** Dois pontos a menos disto, em m, são o mesmo lugar — escolha 1. */
const LUGAR_IGUAL = 0.01;

/** Folga de ponto flutuante nas comparações de profundidade e de passo. */
const FOLGA = 1e-9;

/** A última estação da seção só se acrescenta quando falta mais do que esta fração do passo — escolha 6. */
const SOBRA_DE_ESTACAO = 0.01;

/**
 * Acima disto a seção sai vazia — escolha 7. Medido (Node 22, as dez verticais do 2609350 a até 30 m de
 * uma linha, com a Γ fatorada uma vez por cota): ≈ 1,3 µs por célula na krigagem e ≈ 1,4 µs no IDW → o
 * limite leva ≈ 1,4 s. (Antes da fatoração por cota a krigagem levava ≈ 4,3 µs por célula.)
 */
export const LIMITE_DE_CELULAS_DA_SECAO = 1_000_000;

/**
 * Acima disto a grade sai vazia — escolha 7 (a tela para antes, em 250.000). Medido (Node 22, as dez
 * verticais do 2609350, com a Γ fatorada uma vez por cota): ≈ 4,4 µs por voxel na krigagem e ≈ 2,4 µs no
 * IDW → o limite leva ≈ 9 s na krigagem e ≈ 5 s no IDW. (Antes da fatoração por cota a krigagem levava
 * ≈ 26 µs por voxel: ≈ 52 s no limite.) O servidor impõe o seu próprio teto; este é só a guarda do motor.
 */
export const LIMITE_DE_VOXELS = 2_000_000;

/**
 * O teto do NÚMERO DE COTAS da seção, da grade e do variograma das cotas — só uma guarda contra o passo
 * vertical minúsculo (1e-9 m daria bilhões de cotas); os limites de células e de voxels, acima, são os
 * que valem para o tamanho.
 */
const LIMITE_DE_COTAS = 100_000;

/** A busca do alcance começa nesta fração do menor h com pares — escolha 5. */
const FRACAO_DO_ALCANCE_MINIMO = 0.1;

/** A grade e o refino do ajuste — escolha 5. */
const GRADE_PEPITA = 21;
const GRADE_CONTRIBUICAO = 21;
const GRADE_ALCANCE = 25;
const VOLTAS_DE_REFINO = 24;

/** Um número finito, ou NaN (nunca Infinity). */
const finito = (x: number): number => (Number.isFinite(x) ? x : NaN);

/** Um número em português, com as casas pedidas (1234,56 → «1.234,56»). */
function numeroPt(x: number, casas: number): string {
  if (!Number.isFinite(x)) return '—';
  const [inteiro, fracao] = Math.abs(x).toFixed(casas).split('.');
  const comPontos = inteiro.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${x < 0 ? '−' : ''}${comPontos}${fracao ? `,${fracao}` : ''}`;
}

/* ── O plano e a cota ──────────────────────────────────────────────────── */

/**
 * O plano local equirretangular no centroide das verticais com coordenada: x = R·Δλ·cos φ₀, y = R·Δφ.
 * Sem coordenada nenhuma, o plano fica na origem (0, 0); coordenada sem número dá NaN.
 */
export function planoLocal(verticais: readonly { latitude: number; longitude: number }[]): PlanoLocal {
  let somaLat = 0, somaLon = 0, n = 0;
  for (const v of verticais) {
    if (!Number.isFinite(v.latitude) || !Number.isFinite(v.longitude)) continue;
    somaLat += v.latitude; somaLon += v.longitude; n++;
  }
  const lat0 = n ? somaLat / n : 0;
  const lon0 = n ? somaLon / n : 0;
  const cos0 = Math.cos(lat0 * GRAUS);
  return {
    lat0, lon0,
    // Uma coordenada sem número tira o ponto inteiro do plano (x E y NaN): meio ponto não existe.
    paraPlano: (lat, lon) => (Number.isFinite(lat) && Number.isFinite(lon)
      ? { x: finito(RAIO_DA_TERRA * (lon - lon0) * GRAUS * cos0), y: finito(RAIO_DA_TERRA * (lat - lat0) * GRAUS) }
      : { x: NaN, y: NaN }),
    paraGeo: (p) => (Number.isFinite(p.x) && Number.isFinite(p.y) && cos0 !== 0
      ? { latitude: finito(lat0 + p.y / RAIO_DA_TERRA / GRAUS), longitude: finito(lon0 + p.x / (RAIO_DA_TERRA * cos0) / GRAUS) }
      : { latitude: NaN, longitude: NaN }),
  };
}

/**
 * O valor de uma vertical numa cota: interpolação linear na profundidade z = cota da vertical − cota.
 * NaN fora do trecho [primeira, última leitura], na leitura sem valor e entre uma leitura sem valor e a
 * vizinha. A leitura a até `FOLGA` de z vale como a própria.
 */
export function valorNaCota(v: VerticalEspacial, cota: number): number {
  const zs = v.profundidade, vs = v.valor;
  const n = Math.min(zs.length, vs.length);
  if (n === 0 || !Number.isFinite(cota) || !Number.isFinite(v.cota)) return NaN;
  const z = v.cota - cota;
  if (!(z >= zs[0] - FOLGA && z <= zs[n - 1] + FOLGA)) return NaN;
  // A última leitura ≤ z, por busca binária.
  let lo = 0, hi = n - 1;
  while (hi - lo > 1) {
    const meio = (lo + hi) >> 1;
    if (zs[meio] <= z) lo = meio; else hi = meio;
  }
  if (Math.abs(z - zs[lo]) <= FOLGA) return finito(vs[lo]);
  if (Math.abs(z - zs[hi]) <= FOLGA) return finito(vs[hi]);
  const za = zs[lo], zb = zs[hi];
  if (!(z > za && z < zb) || zb - za <= 0) return NaN;
  return finito(vs[lo] + ((vs[hi] - vs[lo]) * (z - za)) / (zb - za));
}

/* ── O variograma ──────────────────────────────────────────────────────── */

/** γ(h) do modelo: 0 em h ≤ 0; esférico até o alcance e o patamar além; exponencial com o alcance prático (escolha 2). */
export function gamaDoModelo(v: Variograma, h: number): number {
  if (!Number.isFinite(h) || !Number.isFinite(v.pepita) || !Number.isFinite(v.patamar) || !Number.isFinite(v.alcance)) return NaN;
  if (h <= 0) return 0;
  const c = v.patamar - v.pepita;
  if (!(v.alcance > 0)) return v.patamar;
  const r = h / v.alcance;
  if (v.modelo === 'esferico') return r >= 1 ? v.patamar : v.pepita + c * (1.5 * r - 0.5 * r * r * r);
  return v.pepita + c * (1 - Math.exp(-3 * r));
}

/** Os pontos com coordenada e valor com número. */
function pontosValidos(pontos: readonly PontoComValor[]): PontoComValor[] {
  return pontos.filter((q) => Number.isFinite(q.p.x) && Number.isFinite(q.p.y) && Number.isFinite(q.valor));
}

/** A maior distância entre os pontos. */
function maiorDistancia(pontos: readonly PontoComValor[]): number {
  let maior = 0;
  for (let i = 0; i < pontos.length; i++) {
    for (let j = i + 1; j < pontos.length; j++) {
      const d = Math.hypot(pontos[i].p.x - pontos[j].p.x, pontos[i].p.y - pontos[j].p.y);
      if (d > maior) maior = d;
    }
  }
  return maior;
}

/** As classes vazias de um experimental: pares 0, γ NaN, h no meio (escolha 3). */
function classesVazias(classes: number, largura: number): ParExperimental[] {
  return Array.from({ length: classes }, (_, k) => ({ h: (k + 0.5) * largura, gama: NaN, pares: 0 }));
}

/**
 * O variograma experimental: `classes` classes de largura igual de 0 a hMax (metade da maior distância
 * quando não vem — escolha 4), com h médio, γ̂ = Σ(Δv)²/(2n) e os pares; as vazias com pares 0 e γ NaN
 * (escolha 3). Sem dois pontos válidos em lugares distintos, ou sem classe, [].
 */
export function variogramaExperimental(pontos: readonly { p: PontoPlano; valor: number }[], classes: number, hMax?: number): ParExperimental[] {
  const nClasses = Number.isFinite(classes) ? Math.floor(classes) : 0;
  if (nClasses < 1) return [];
  const validos = pontosValidos(pontos);
  const limite = hMax === undefined ? maiorDistancia(validos) / 2 : hMax;
  if (!(limite > 0) || !Number.isFinite(limite) || validos.length < 2) return [];
  const largura = limite / nClasses;
  const somaH = new Float64Array(nClasses), somaD2 = new Float64Array(nClasses), pares = new Uint32Array(nClasses);
  for (let i = 0; i < validos.length; i++) {
    for (let j = i + 1; j < validos.length; j++) {
      const d = Math.hypot(validos[i].p.x - validos[j].p.x, validos[i].p.y - validos[j].p.y);
      if (d > limite + FOLGA) continue;
      const k = Math.min(Math.floor(d / largura), nClasses - 1);
      const dv = validos[i].valor - validos[j].valor;
      somaH[k] += d; somaD2[k] += dv * dv; pares[k]++;
    }
  }
  return classesVazias(nClasses, largura).map((vazia, k) =>
    pares[k] ? { h: somaH[k] / pares[k], gama: finito(somaD2[k] / (2 * pares[k])), pares: pares[k] } : vazia);
}

/** O critério de Cressie: Σ n·(γ̂/γ_modelo − 1)²; Infinity onde o modelo zera e o dado não. */
function criterioDeCressie(exp: readonly ParExperimental[], v: Variograma): number {
  let soma = 0;
  for (const c of exp) {
    const g = gamaDoModelo(v, c.h);
    if (!(g > 0)) { if (c.gama === 0) continue; return Infinity; }
    const r = c.gama / g - 1;
    soma += c.pares * r * r;
  }
  return soma;
}

/**
 * Ajusta um modelo ao experimental por mínimos quadrados ponderados de Cressie (peso = pares/γ_modelo²),
 * por busca em grade e refino (escolha 5). Com menos de 3 classes com pares, `ajustado: false` e o
 * motivo. A pepita pura (o modelo constante, custo em forma fechada) ganha no empate com a busca e vem
 * com `pepita = patamar`, o alcance = o maior h com pares e o motivo «sem estrutura espacial»; o motivo
 * também vem quando o alcance fica num dos limites da busca (0,1 × o menor h e 2 × o maior h com pares).
 */
export function ajustarVariograma(exp: readonly ParExperimental[], modelo: ModeloDeVariograma): Variograma & { ajustado: boolean; motivo: string | null } {
  const uteis = exp.filter((c) => c.pares > 0 && Number.isFinite(c.gama) && c.gama >= 0 && Number.isFinite(c.h) && c.h > 0);
  const semAjuste = (motivo: string) => ({ modelo, pepita: NaN, patamar: NaN, alcance: NaN, ajustado: false, motivo });
  if (uteis.length < 3) return semAjuste(`menos de 3 classes com pares (${uteis.length}): o variograma não se ajusta`);
  let gamaMax = 0, hMin = Infinity, hMax = 0;
  for (const c of uteis) { gamaMax = Math.max(gamaMax, c.gama); hMin = Math.min(hMin, c.h); hMax = Math.max(hMax, c.h); }
  if (!(gamaMax > 0)) return semAjuste('todas as classes com γ = 0: sem variação entre as verticais');
  const alcanceMin = hMin * FRACAO_DO_ALCANCE_MINIMO;
  const alcanceMax = 2 * hMax;
  const custo = (pepita: number, contribuicao: number, alcance: number) =>
    criterioDeCressie(uteis, { modelo, pepita, patamar: pepita + contribuicao, alcance });

  // A grade.
  let melhor = { pepita: 0, contribuicao: gamaMax, alcance: alcanceMax, custo: Infinity };
  const passoP = gamaMax / (GRADE_PEPITA - 1), passoC = (2 * gamaMax) / (GRADE_CONTRIBUICAO - 1);
  const passoA = (alcanceMax - alcanceMin) / (GRADE_ALCANCE - 1);
  for (let i = 0; i < GRADE_PEPITA; i++) {
    for (let j = 0; j < GRADE_CONTRIBUICAO; j++) {
      for (let k = 0; k < GRADE_ALCANCE; k++) {
        const pepita = i * passoP, contribuicao = j * passoC, alcance = alcanceMin + k * passoA;
        const c = custo(pepita, contribuicao, alcance);
        if (c < melhor.custo) melhor = { pepita, contribuicao, alcance, custo: c };
      }
    }
  }
  // O refino: busca local nos 26 vizinhos com o passo caindo pela metade, dentro dos limites.
  let dP = passoP, dC = passoC, dA = passoA;
  for (let volta = 0; volta < VOLTAS_DE_REFINO; volta++) {
    let mexeu = true;
    while (mexeu) {
      mexeu = false;
      for (let i = -1; i <= 1; i++) {
        for (let j = -1; j <= 1; j++) {
          for (let k = -1; k <= 1; k++) {
            if (!i && !j && !k) continue;
            const pepita = Math.min(Math.max(melhor.pepita + i * dP, 0), gamaMax);
            const contribuicao = Math.min(Math.max(melhor.contribuicao + j * dC, 0), 2 * gamaMax);
            const alcance = Math.min(Math.max(melhor.alcance + k * dA, alcanceMin), alcanceMax);
            const c = custo(pepita, contribuicao, alcance);
            if (c < melhor.custo) { melhor = { pepita, contribuicao, alcance, custo: c }; mexeu = true; }
          }
        }
      }
    }
    dP /= 2; dC /= 2; dA /= 2;
  }
  // A pepita pura em forma fechada: p = Σ n γ̂² / Σ n γ̂ minimiza Σ n (γ̂/p − 1)². Ganha no empate (a
  // hipótese mais simples): abaixo do menor h a busca não distingue «pepita p» de «contribuição p com
  // alcance minúsculo», e sem o desempate sairia um alcance fictício no limite de baixo.
  const SEM_ESTRUTURA = 'o patamar ficou na pepita: sem estrutura espacial nos dados';
  let somaNG = 0, somaNG2 = 0;
  for (const c of uteis) { somaNG += c.pares * c.gama; somaNG2 += c.pares * c.gama * c.gama; }
  const pepitaPura = somaNG2 / somaNG;
  const custoPuro = custo(pepitaPura, 0, hMax);
  if (Number.isFinite(custoPuro) && !(melhor.custo < custoPuro * (1 - 1e-9))) {
    return { modelo, pepita: pepitaPura, patamar: pepitaPura, alcance: hMax, ajustado: true, motivo: SEM_ESTRUTURA };
  }
  if (!Number.isFinite(melhor.custo)) return semAjuste('nenhum modelo da busca serve ao experimental');
  let motivo: string | null = null;
  if (melhor.alcance >= alcanceMax - dA * 2) {
    motivo = `o alcance ficou no limite da busca (${numeroPt(alcanceMax, 2)} m): os dados não o definem`;
  } else if (melhor.alcance <= alcanceMin + dA * 2) {
    motivo = `o alcance ficou no limite inferior da busca (${numeroPt(alcanceMin, 2)} m): os dados não o definem`;
  } else if (melhor.contribuicao <= dC * 2) {
    motivo = SEM_ESTRUTURA;
  }
  return { modelo, pepita: melhor.pepita, patamar: melhor.pepita + melhor.contribuicao, alcance: melhor.alcance, ajustado: true, motivo };
}

/* ── IDW e krigagem ────────────────────────────────────────────────────── */

/** A média dos valores dos pontos a até `LUGAR_IGUAL` do alvo, ou NaN se não há nenhum. */
function noProprioLugar(pontos: readonly PontoComValor[], alvo: PontoPlano): number {
  let soma = 0, n = 0;
  for (const q of pontos) {
    if (Math.hypot(q.p.x - alvo.x, q.p.y - alvo.y) <= LUGAR_IGUAL) { soma += q.valor; n++; }
  }
  return n ? soma / n : NaN;
}

/**
 * O inverso da distância: Σ wᵢvᵢ / Σ wᵢ, wᵢ = dᵢ^−potência (2 de partida). No próprio ponto devolve o
 * valor dele (a média, se há mais de um ali — escolha 1); sem ponto válido, NaN.
 */
export function idw(pontos: readonly { p: PontoPlano; valor: number }[], alvo: PontoPlano, potencia?: number): number {
  const validos = pontosValidos(pontos);
  if (!validos.length || !Number.isFinite(alvo.x) || !Number.isFinite(alvo.y)) return NaN;
  const proprio = noProprioLugar(validos, alvo);
  if (Number.isFinite(proprio)) return proprio;
  const p = potencia === undefined || !Number.isFinite(potencia) ? POTENCIA_PADRAO : potencia;
  let somaW = 0, somaWV = 0;
  for (const q of validos) {
    const w = Math.pow(Math.hypot(q.p.x - alvo.x, q.p.y - alvo.y), -p);
    somaW += w; somaWV += w * q.valor;
  }
  return finito(somaWV / somaW);
}

/** A fatoração P·A = L·U guardada no lugar de A (os multiplicadores abaixo da diagonal) e a permutação das linhas. */
type FatoracaoLU = { lu: Float64Array[]; perm: Int32Array };

/**
 * Fatora A (no lugar) por eliminação de Gauss com pivô parcial, guardando os multiplicadores abaixo da
 * diagonal e a ordem das linhas; null se a matriz é singular (pivô menor que 1e-12 × a maior entrada).
 * A linha inteira troca de lugar (os multiplicadores já guardados junto), como no LAPACK: a mesma conta,
 * passo a passo, da eliminação aplicada a um lado direito de cada vez.
 */
function fatorarLU(a: Float64Array[]): FatoracaoLU | null {
  const n = a.length;
  let maior = 0;
  for (const linha of a) for (const x of linha) { if (!Number.isFinite(x)) return null; maior = Math.max(maior, Math.abs(x)); }
  const limite = 1e-12 * (maior || 1);
  const perm = Int32Array.from({ length: n }, (_, i) => i);
  for (let col = 0; col < n; col++) {
    let piv = col;
    for (let i = col + 1; i < n; i++) if (Math.abs(a[i][col]) > Math.abs(a[piv][col])) piv = i;
    if (Math.abs(a[piv][col]) <= limite) return null;
    if (piv !== col) {
      [a[piv], a[col]] = [a[col], a[piv]];
      const t = perm[piv]; perm[piv] = perm[col]; perm[col] = t;
    }
    for (let i = col + 1; i < n; i++) {
      const f = a[i][col] / a[col][col];
      a[i][col] = f;
      if (f === 0) continue;
      for (let j = col + 1; j < n; j++) a[i][j] -= f * a[col][j];
    }
  }
  return { lu: a, perm };
}

/** Resolve A·x = b com a fatoração guardada: L·y = P·b para a frente, U·x = y para trás. */
function resolverLU({ lu, perm }: FatoracaoLU, b: ArrayLike<number>): Float64Array {
  const n = lu.length;
  const y = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    let s = b[perm[i]];
    for (let j = 0; j < i; j++) s -= lu[i][j] * y[j];
    y[i] = s;
  }
  const x = new Float64Array(n);
  for (let i = n - 1; i >= 0; i--) {
    let s = y[i];
    for (let j = i + 1; j < n; j++) s -= lu[i][j] * x[j];
    x[i] = s / lu[i][i];
  }
  return x;
}

/**
 * O krigador de um conjunto de pontos: monta e fatora UMA VEZ a matriz Γ do sistema (n + 1) × (n + 1)
 * com o multiplicador de Lagrange e devolve a função que, para cada alvo, só resolve o lado direito —
 * é o que a seção e a grade usam, cota a cota (escolha 7). Singular (dois pontos no mesmo lugar, γ ≡ 0)
 * → NaN em todo alvo. No próprio ponto dado devolve o valor dele com variância = pepita (escolha 2).
 * σ² = Σ λᵢ γ(hᵢ₀) + μ.
 */
function krigadorDaCota(pontos: readonly PontoComValor[], v: Variograma): (alvo: PontoPlano) => { valor: number; variancia: number } {
  const nada = { valor: NaN, variancia: NaN };
  const validos = pontosValidos(pontos);
  if (!validos.length || !Number.isFinite(v.pepita) || !Number.isFinite(v.patamar) || !Number.isFinite(v.alcance)) return () => nada;
  const n = validos.length;
  const a: Float64Array[] = Array.from({ length: n + 1 }, () => new Float64Array(n + 1));
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const g = gamaDoModelo(v, Math.hypot(validos[i].p.x - validos[j].p.x, validos[i].p.y - validos[j].p.y));
      a[i][j] = g; a[j][i] = g;
    }
    a[i][n] = 1; a[n][i] = 1;
  }
  const fatorada = fatorarLU(a);
  const gama0 = new Float64Array(n + 1);
  return (alvo) => {
    if (!Number.isFinite(alvo.x) || !Number.isFinite(alvo.y)) return nada;
    const proprio = noProprioLugar(validos, alvo);
    if (Number.isFinite(proprio)) return { valor: proprio, variancia: v.pepita };
    if (!fatorada) return nada;
    for (let i = 0; i < n; i++) gama0[i] = gamaDoModelo(v, Math.hypot(validos[i].p.x - alvo.x, validos[i].p.y - alvo.y));
    gama0[n] = 1;
    const x = resolverLU(fatorada, gama0);
    let valor = 0, variancia = x[n];
    for (let i = 0; i < n; i++) { valor += x[i] * validos[i].valor; variancia += x[i] * gama0[i]; }
    return { valor: finito(valor), variancia: finito(variancia) };
  };
}

/**
 * A krigagem ordinária num alvo: o sistema (n + 1) × (n + 1) com o multiplicador de Lagrange, por
 * eliminação de Gauss com pivô (`krigadorDaCota`, montado e fatorado só para este alvo); singular (dois
 * pontos no mesmo lugar, γ ≡ 0) → NaN. No próprio ponto dado devolve o valor dele com variância = pepita
 * (escolha 2). σ² = Σ λᵢ γ(hᵢ₀) + μ.
 */
export function krigagemOrdinaria(pontos: readonly { p: PontoPlano; valor: number }[], alvo: PontoPlano, v: Variograma): { valor: number; variancia: number } {
  return krigadorDaCota(pontos, v)(alvo);
}

/** O estimador de um conjunto de pontos pelo método pedido: a função que interpola num alvo. */
function estimador(metodo: MetodoEspacial, pontos: readonly PontoComValor[]): (alvo: PontoPlano) => number {
  if (metodo.tipo === 'idw') return (alvo) => idw(pontos, alvo, metodo.potencia);
  const krigar = krigadorDaCota(pontos, metodo.variograma);
  return (alvo) => krigar(alvo).valor;
}

/** Junta os pontos no mesmo lugar (a até `LUGAR_IGUAL`) pela média dos valores — escolha 1. */
function agruparPorLugar(pontos: readonly PontoComValor[]): PontoComValor[] {
  const grupos: { p: PontoPlano; soma: number; n: number }[] = [];
  for (const q of pontos) {
    const g = grupos.find((x) => Math.hypot(x.p.x - q.p.x, x.p.y - q.p.y) <= LUGAR_IGUAL);
    if (g) { g.soma += q.valor; g.n++; } else grupos.push({ p: q.p, soma: q.valor, n: 1 });
  }
  return grupos.map((g) => ({ p: g.p, valor: g.soma / g.n }));
}

/* ── A seção, o COV, a grade e o variograma das cotas ──────────────────── */

/** As verticais com coordenada e cota com número. */
function verticaisValidas(verticais: readonly VerticalEspacial[]): VerticalEspacial[] {
  return verticais.filter((v) => Number.isFinite(v.latitude) && Number.isFinite(v.longitude) && Number.isFinite(v.cota)
    && v.profundidade.length > 0 && v.valor.length > 0);
}

/** As cotas de `passo` em `passo`, da boca mais alta ao fundo mais fundo (começando na boca); [] sem passo que sirva. */
function cotasDasVerticais(verticais: readonly VerticalEspacial[], passo: number): Float64Array {
  if (!(passo > 0) || !Number.isFinite(passo) || !verticais.length) return new Float64Array(0);
  let topo = -Infinity, fundo = Infinity;
  for (const v of verticais) {
    topo = Math.max(topo, v.cota);
    fundo = Math.min(fundo, v.cota - v.profundidade[v.profundidade.length - 1]);
  }
  if (!Number.isFinite(topo) || !Number.isFinite(fundo) || fundo > topo) return new Float64Array(0);
  const n = Math.floor((topo - fundo) / passo + FOLGA) + 1;
  if (!Number.isFinite(n) || n > LIMITE_DE_COTAS) return new Float64Array(0);
  return Float64Array.from({ length: n }, (_, k) => topo - k * passo);
}

/** Os pontos (em planta) com valor numa cota, as do mesmo lugar pela média. */
function pontosNaCota(verticais: readonly VerticalEspacial[], posicoes: readonly PontoPlano[], cota: number): PontoComValor[] {
  const pontos: PontoComValor[] = [];
  for (let i = 0; i < verticais.length; i++) {
    const valor = valorNaCota(verticais[i], cota);
    if (Number.isFinite(valor)) pontos.push({ p: posicoes[i], valor });
  }
  return agruparPorLugar(pontos);
}

/** O COV: desvio-padrão amostral / |média| dos valores com número; NaN com menos de 2 valores ou média 0. */
export function covNaCota(valores: readonly number[]): number {
  const uteis = valores.filter((x) => Number.isFinite(x));
  if (uteis.length < 2) return NaN;
  const media = uteis.reduce((s, x) => s + x, 0) / uteis.length;
  if (media === 0) return NaN;
  const soma2 = uteis.reduce((s, x) => s + (x - media) * (x - media), 0);
  return finito(Math.sqrt(soma2 / (uteis.length - 1)) / Math.abs(media));
}

/** A projeção de um ponto na polilinha: a distância ao longo (no trecho mais perto) e o afastamento. */
function projetarNaLinha(p: PontoPlano, linha: readonly PontoPlano[], acumulado: readonly number[]): { distancia: number; afastamento: number } {
  if (linha.length === 1) return { distancia: 0, afastamento: Math.hypot(p.x - linha[0].x, p.y - linha[0].y) };
  let melhor = { distancia: NaN, afastamento: Infinity };
  for (let s = 0; s + 1 < linha.length; s++) {
    const a = linha[s], b = linha[s + 1];
    const dx = b.x - a.x, dy = b.y - a.y;
    const l2 = dx * dx + dy * dy;
    const t = l2 > 0 ? Math.min(Math.max(((p.x - a.x) * dx + (p.y - a.y) * dy) / l2, 0), 1) : 0;
    const fx = a.x + t * dx, fy = a.y + t * dy;
    const afastamento = Math.hypot(p.x - fx, p.y - fy);
    if (afastamento < melhor.afastamento - FOLGA) melhor = { distancia: acumulado[s] + t * Math.sqrt(l2), afastamento };
  }
  return melhor;
}

const SECAO_VAZIA = (verticais: Secao['verticais'] = []): Secao =>
  ({ distancias: new Float64Array(0), cotas: new Float64Array(0), valores: new Float64Array(0), verticais, cov: new Float64Array(0) });

/**
 * A seção ao longo de uma polilinha (escolha 6): projeta cada vertical (distância ao longo e
 * afastamento), entram as de afastamento ≤ máximo (30 m de partida); em cada cota interpola em 1D ao
 * longo da linha com as verticais que têm valor ali. Vazia sem vertical perto, sem linha, com um vértice
 * da linha sem número, com passo que não serve ou acima de `LIMITE_DE_CELULAS_DA_SECAO` células.
 */
export function secaoAoLongoDaLinha(
  verticais: readonly VerticalEspacial[],
  linha: readonly { latitude: number; longitude: number }[],
  x: { metodo: MetodoEspacial; passoHorizontal: number; passoVertical: number; afastamentoMaximo?: number },
): Secao {
  const validas = verticaisValidas(verticais);
  // Um vértice sem número não se descarta (emendar os vizinhos seria outra linha): a seção sai vazia.
  const linhaInteira = linha.every((q) => Number.isFinite(q.latitude) && Number.isFinite(q.longitude));
  if (!validas.length || !linha.length || !linhaInteira) return SECAO_VAZIA();
  const plano = planoLocal(validas);
  const polilinha = linha.map((q) => plano.paraPlano(q.latitude, q.longitude));
  const acumulado = [0];
  for (let s = 1; s < polilinha.length; s++) {
    acumulado.push(acumulado[s - 1] + Math.hypot(polilinha[s].x - polilinha[s - 1].x, polilinha[s].y - polilinha[s - 1].y));
  }
  const comprimento = acumulado[acumulado.length - 1];
  const maximo = x.afastamentoMaximo === undefined ? AFASTAMENTO_MAXIMO_PADRAO : x.afastamentoMaximo;
  const perto: { v: VerticalEspacial; distancia: number; afastamento: number }[] = [];
  for (const v of validas) {
    const pr = projetarNaLinha(plano.paraPlano(v.latitude, v.longitude), polilinha, acumulado);
    if (Number.isFinite(pr.distancia) && pr.afastamento <= maximo) perto.push({ v, ...pr });
  }
  perto.sort((a, b) => a.distancia - b.distancia || (a.v.codigo < b.v.codigo ? -1 : a.v.codigo > b.v.codigo ? 1 : 0));
  const lista = perto.map((q) => ({ codigo: q.v.codigo, distancia: q.distancia, afastamento: q.afastamento }));
  if (!perto.length || !(x.passoHorizontal > 0) || !Number.isFinite(x.passoHorizontal)) return SECAO_VAZIA(lista);
  const cotas = cotasDasVerticais(perto.map((q) => q.v), x.passoVertical);
  if (!cotas.length) return SECAO_VAZIA(lista);
  // As estações de passo em passo; a última é o fim exato da linha: acrescentada quando falta mais de
  // 1 % do passo, e no lugar da última regular quando falta menos (o resto de ponto flutuante de uma
  // linha de 100,00001 m não vale uma estação a mais).
  const nEstacoes = Math.floor(comprimento / x.passoHorizontal + FOLGA) + 1;
  const faltaFim = comprimento - (nEstacoes - 1) * x.passoHorizontal > SOBRA_DE_ESTACAO * x.passoHorizontal;
  const nd = nEstacoes + (faltaFim ? 1 : 0);
  if (!Number.isFinite(nd) || nd * cotas.length > LIMITE_DE_CELULAS_DA_SECAO) return SECAO_VAZIA(lista);
  const distancias = Float64Array.from({ length: nd }, (_, i) => (i < nd - 1 ? i * x.passoHorizontal : comprimento));
  const posicoes = perto.map((q) => ({ x: q.distancia, y: 0 }));
  const valores = new Float64Array(cotas.length * nd).fill(NaN);
  const cov = new Float64Array(cotas.length).fill(NaN);
  const vs = perto.map((q) => q.v);
  for (let ic = 0; ic < cotas.length; ic++) {
    const pontos = pontosNaCota(vs, posicoes, cotas[ic]);
    cov[ic] = covNaCota(vs.map((v) => valorNaCota(v, cotas[ic])));
    if (!pontos.length) continue;
    const estimar = estimador(x.metodo, pontos);
    for (let id = 0; id < nd; id++) valores[ic * nd + id] = estimar({ x: distancias[id], y: 0 });
  }
  return { distancias, cotas, valores, verticais: lista, cov };
}

const GRADE_VAZIA = (passo: GradeDeVoxels['passo']): GradeDeVoxels =>
  ({ origem: { x: NaN, y: NaN, cota: NaN }, passo, n: { x: 0, y: 0, z: 0 }, valores: new Float32Array(0) });

/**
 * A grade de voxels (escolha 7): a caixa das verticais em planta mais a margem (meio passo quando não
 * vem), as cotas da boca mais alta ao fundo mais fundo; por cota, interpolação 2D com as verticais que
 * têm valor ali (sem nenhuma, NaN). Vazia sem vertical, com passo que não serve ou acima de
 * `LIMITE_DE_VOXELS`.
 */
export function gradeDeVoxels(
  verticais: readonly VerticalEspacial[],
  x: { metodo: MetodoEspacial; passo: { x: number; y: number; z: number }; margem?: number },
): GradeDeVoxels {
  const validas = verticaisValidas(verticais);
  const { passo } = x;
  if (!validas.length || !(passo.x > 0) || !(passo.y > 0) || !(passo.z > 0)
    || !Number.isFinite(passo.x) || !Number.isFinite(passo.y) || !Number.isFinite(passo.z)) return GRADE_VAZIA(passo);
  const plano = planoLocal(validas);
  const posicoes = validas.map((v) => plano.paraPlano(v.latitude, v.longitude));
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const p of posicoes) { minX = Math.min(minX, p.x); maxX = Math.max(maxX, p.x); minY = Math.min(minY, p.y); maxY = Math.max(maxY, p.y); }
  const margemX = x.margem === undefined ? passo.x / 2 : x.margem;
  const margemY = x.margem === undefined ? passo.y / 2 : x.margem;
  if (!Number.isFinite(margemX) || !Number.isFinite(margemY) || margemX < 0 || margemY < 0) return GRADE_VAZIA(passo);
  const cotas = cotasDasVerticais(validas, passo.z);
  if (!cotas.length) return GRADE_VAZIA(passo);
  const origemX = minX - margemX, origemY = minY - margemY;
  // O último nó cobre máx + margem (teto): pode ficar aquém só de `LUGAR_IGUAL` (1 cm, o mesmo lugar da
  // escolha 1) — a largura a 20,00003 m, que o plano local dá para verticais postas a 20 m noutro plano,
  // não vale um nó a mais.
  const nx = Math.ceil((maxX + margemX - origemX - LUGAR_IGUAL) / passo.x) + 1;
  const ny = Math.ceil((maxY + margemY - origemY - LUGAR_IGUAL) / passo.y) + 1;
  const nz = cotas.length;
  const total = nx * ny * nz;
  if (!Number.isFinite(total) || total > LIMITE_DE_VOXELS) return GRADE_VAZIA(passo);
  const valores = new Float32Array(total).fill(NaN);
  // As cotas vêm da boca mais alta para baixo; o índice iz cresce do fundo para a boca.
  for (let k = 0; k < nz; k++) {
    const iz = nz - 1 - k;
    const pontos = pontosNaCota(validas, posicoes, cotas[k]);
    if (!pontos.length) continue;
    const estimar = estimador(x.metodo, pontos);
    for (let iy = 0; iy < ny; iy++) {
      for (let ix = 0; ix < nx; ix++) {
        valores[ix + nx * (iy + ny * iz)] = estimar({ x: origemX + ix * passo.x, y: origemY + iy * passo.y });
      }
    }
  }
  return { origem: { x: origemX, y: origemY, cota: cotas[nz - 1] }, passo: { ...passo }, n: { x: nx, y: ny, z: nz }, valores };
}

/**
 * O variograma das cotas (escolha 8): em cada cota de `passoVertical` em `passoVertical`, da boca mais
 * alta ao fundo mais fundo, o experimental das verticais com valor ali (distância no plano local, hMax =
 * metade da maior distância em planta ou o dado); as `classes` classes somadas pelos pares, com h e γ
 * médios ponderados — todas, as vazias como no experimental. Com menos de dois lugares distintos, [].
 */
export function variogramaDasCotas(verticais: readonly VerticalEspacial[], x: { passoVertical: number; classes: number; hMax?: number }): ParExperimental[] {
  const nClasses = Number.isFinite(x.classes) ? Math.floor(x.classes) : 0;
  const validas = verticaisValidas(verticais);
  if (nClasses < 1 || validas.length < 2) return [];
  const plano = planoLocal(validas);
  const posicoes = validas.map((v) => plano.paraPlano(v.latitude, v.longitude));
  const lugares = agruparPorLugar(posicoes.map((p) => ({ p, valor: 0 })));
  const limite = x.hMax === undefined ? maiorDistancia(lugares) / 2 : x.hMax;
  if (lugares.length < 2 || !(limite > 0) || !Number.isFinite(limite)) return [];
  const cotas = cotasDasVerticais(validas, x.passoVertical);
  if (!cotas.length) return [];
  const largura = limite / nClasses;
  const somaH = new Float64Array(nClasses), somaG = new Float64Array(nClasses), pares = new Float64Array(nClasses);
  for (const cota of cotas) {
    const pontos = pontosNaCota(validas, posicoes, cota);
    if (pontos.length < 2) continue;
    const exp = variogramaExperimental(pontos, nClasses, limite);
    exp.forEach((c, k) => {
      if (!c.pares) return;
      somaH[k] += c.h * c.pares; somaG[k] += c.gama * c.pares; pares[k] += c.pares;
    });
  }
  return classesVazias(nClasses, largura).map((vazia, k) =>
    pares[k] ? { h: somaH[k] / pares[k], gama: finito(somaG[k] / pares[k]), pares: pares[k] } : vazia);
}
